import { createHttpClient } from '@adapters/HttpClient';
import { RulesApiAdapter } from '@modules/configuration/Rules/adapters/RulesApiAdapter';
import { PreviewShippingRuleResponse } from '@modules/configuration/Rules/models/PreviewShippingRule';
import type { Order } from '@modules/Orders/List/models/Orders';
import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { useToast } from '@shared/hooks/use-toast';
import { apiClientWH } from '@shared/services';
import { cn } from '@shared/utils/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShippingRuleOrderSelect } from '../interaction/ShippingRuleOrderSelect';

export type ShippingRulePreviewModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Team (cliente) cuyas reglas se simulan: filtra el listado de pedidos. */
  teamId: number;
};

export const ShippingRulePreviewModal = ({ open, onOpenChange, teamId }: ShippingRulePreviewModalProps) => {
  const { t } = useTranslation('rulesModule');
  const { toast } = useToast();
  const [orderId, setOrderId] = useState<number | null>(null);

  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<PreviewShippingRuleResponse | null>(null);

  const api = useMemo(() => new RulesApiAdapter(createHttpClient(apiClientWH)), []);

  useEffect(() => {
    if (!open) {
      return;
    }
    setResult(null);
    setOrderId(null);
  }, [open]);

  const onSelectOrder = useCallback((order: Order) => {
    setResult(null);
    setOrderId(order.id);
  }, []);

  const onSubmit = useCallback(async () => {
    if (orderId == null) {
      toast({ title: t('clientList.preview.toastSelectOrder'), variant: 'destructive' });
      return;
    }
    if (teamId < 1) {
      toast({ title: t('clientList.preview.error'), variant: 'destructive' });
      return;
    }

    setPending(true);
    try {
      const data = await api.previewShippingRule({
        order_id: orderId,
        rules_team_id: teamId,
      });
      setResult({
        matched_rule: data.matched_rule ?? null,
        evaluations: data.evaluations ?? [],
      });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || t('clientList.preview.error');
      toast({ title: t('clientList.preview.error'), description: String(msg), variant: 'destructive' });
    } finally {
      setPending(false);
    }
  }, [api, orderId, teamId, t, toast]);

  const handleChooseAnotherOrder = useCallback(() => {
    setResult(null);
    setOrderId(null);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(90vh,880px)] max-w-3xl flex-col gap-0 overflow-hidden p-0"
        data-testid="shipping-rule-preview-modal"
      >
        <DialogHeader className="shrink-0 space-y-1 border-b border-border px-6 py-4">
          <DialogTitle className="text-left">{t('clientList.preview.title')}</DialogTitle>
          <DialogDescription className="text-left text-sm">
            {t('clientList.preview.descriptionOrderOnly')}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {!result && (
            <div className="space-y-2">
              <ShippingRuleOrderSelect
                teamId={teamId}
                orderId={orderId}
                onSelect={onSelectOrder}
                dataTestId="rule-preview-order-select"
              />
              <p className="text-xs text-muted-foreground">{t('clientList.preview.orderHelper')}</p>
            </div>
          )}

          {result && (
            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-sm font-medium text-foreground">{t('clientList.preview.matched')}</p>
              {result.matched_rule ? (
                <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                  <span className="font-semibold text-foreground">#{result.matched_rule.priority} </span>
                  {result.matched_rule.name}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('clientList.preview.noMatch')}</p>
              )}

              <p className="pt-1 text-sm font-medium text-foreground">{t('clientList.preview.evaluations')}</p>
              <div className="max-h-56 overflow-auto rounded-md border border-border">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50 text-xs text-muted-foreground">
                      <th className="px-2 py-1.5 font-medium">#</th>
                      <th className="px-2 py-1.5 font-medium">{t('clientList.preview.colName')}</th>
                      <th className="px-2 py-1.5 font-medium text-center">{t('clientList.preview.colMatches')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.evaluations.map((row) => (
                      <tr key={row.rule_id} className="border-b border-border/60 last:border-0">
                        <td className="px-2 py-1.5 tabular-nums text-muted-foreground">{row.priority}</td>
                        <td className="px-2 py-1.5">{row.name}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span
                            className={cn(
                              'inline-flex rounded px-1.5 py-0.5 text-xs font-medium',
                              row.matches ? 'bg-primary/15 text-foreground' : 'bg-muted/80 text-muted-foreground',
                            )}
                          >
                            {row.matches ? t('clientList.preview.yes') : t('clientList.preview.no')}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border px-6 py-4 sm:justify-end sm:gap-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('modal.buttons.cancel')}
          </Button>
          {result ? (
            <Button type="button" variant="outline" onClick={handleChooseAnotherOrder}>
              {t('clientList.preview.chooseAnotherOrder')}
            </Button>
          ) : (
            <Button type="button" variant="primary" disabled={pending || orderId == null} onClick={onSubmit}>
              {pending ? t('clientList.preview.running') : t('clientList.preview.run')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
